import { useRef, useState } from "react";

import { google } from "@smart/bridge-images-dom";
import { AddressPrediction } from "@smart/bridge-resources-basic";
import { useDebouncedValue } from "@smart/itops-hooks-dom";
import { SearchFieldInput, Spinner } from "@smart/itops-sb-design-system-dom";
import { specialChars } from "@smart/itops-utils-basic";

import { FieldGroup } from "./field-group";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { fieldName } from "../../hooks";
import { LookupOptions } from "../../types";

export const AddressField = ({
  field,
  index,
  innerRef,
  value,
  error,
  loading: fieldLoading,
  disabled,
  onChange,
  onBlur,
  addressLookup: { search, load },
}: FieldProps<HTMLInputElement> & {
  addressLookup: LookupOptions["addressLookup"];
}) => {
  const sessionKey = useRef<string | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const searchValue = useDebouncedValue(value?.formattedAddress ?? "", 500);
  const { result, loading } = search({
    sessionKey: sessionKey.current,
    search: searchValue,
  });
  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  const onSelect = async (item: AddressPrediction) => {
    onChange({ formattedAddress: item.description });
    setFetching(true);
    const place = await load({
      placeId: item.place_id,
      sessionKey: sessionKey.current,
    });
    onChange(place ?? null);
    sessionKey.current = undefined;
    setFetching(false);
    onBlur();
  };

  const getItems = () => {
    const searchItems =
      result?.map((item) => ({
        label: item.place_id, // label acts as the key for the item
        labelOverride: (
          <>
            <strong>{item.structured_formatting.main_text}</strong>
            <span>
              {specialChars.nbsp}
              {item.structured_formatting.secondary_text}
            </span>
          </>
        ),
        onClick: async () => {
          await onSelect(item);
        },
      })) || [];

    return [
      ...searchItems,
      {
        className: "pointer-events-none",
        label: "google-logo",
        labelOverride: (
          <div className="flex items-center justify-end gap-small">
            {loading && <Spinner size="regular" color="primary" />}
            <img
              src={google}
              alt="Powered by Google"
              className="w-[6.6rem] ml-auto"
            />
          </div>
        ),
      },
    ];
  };

  return (
    <FieldWrapper disabled={disabled} isLoading={fieldLoading}>
      <FieldGroup
        id={id}
        hint={field.hint ?? undefined}
        label={<Label {...field} />}
      >
        <SearchFieldInput
          dataTestId="address-field-input"
          ref={innerRef}
          id={id}
          name={id}
          aria-invalid={!!error}
          aria-errormessage={errorId ?? undefined}
          error={!!error}
          message={error ?? undefined}
          aria-autocomplete="list"
          value={value?.formattedAddress ?? ""}
          onChange={(e) => {
            if (!sessionKey.current)
              sessionKey.current = `${field.uri}-${Date.now()}`;
            onChange({ formattedAddress: e.target.value });
          }}
          autoComplete="off"
          fetching={loading && !!sessionKey.current}
          disabled={fetching}
          onClear={() => {
            onChange(null);
            onBlur();
          }}
          options={getItems()}
          leftIcon={null}
        />
      </FieldGroup>
    </FieldWrapper>
  );
};
